/**
 * Shared human-readable labels for enum-like values returned by the API.
 *
 * `humanizeEnum` is the generic fallback (snake_case → Title Case). Values
 * whose auto-generated form is wrong or unhelpful get explicit entries in
 * `ENUM_LABELS`; add to that map rather than special-casing a screen.
 */

const SMALL_WORDS = new Set(['and', 'or', 'of', 'the', 'a', 'an', 'to', 'in', 'on', 'for', 'per']);

export const ENUM_LABELS: Record<string, string> = {
  // Project billing
  time_and_materials: 'Time and Materials',
  fixed_price: 'Fixed Price',
  cost_plus: 'Cost Plus',
  per_diem: 'Per Diem',

  // Dispatch preflight blocker keys
  expired_primary_cert: 'Expired primary cert',
  expired_ancillary_cert: 'Expired ancillary cert',
  expired_medical_doc: 'Expired medical document',

  // Safety document statuses
  pending_signatures: 'Pending Signatures',
  pending_review: 'Pending Review',
  in_progress: 'In Progress',
};

/** Model type slugs (Django `content_type.model`) as they appear in activity feeds. */
export const ACTIVITY_MODEL_LABELS: Record<string, string> = {
  jsasignature: 'JSA signature',
  jsa: 'JSA',
  payrollrun: 'payroll run',
  payrollentry: 'payroll entry',
  techcertification: 'certification',
  techprofile: 'employee',
  safeworkplan: 'safe work plan',
  nearmiss: 'near miss',
  toolboxtalk: 'toolbox talk',
  rescueplan: 'rescue plan',
  incidentreport: 'incident report',
  controlleddocument: 'controlled document',
  documentrevision: 'document revision',
  organizationsettings: 'organization settings',
  modulerole: 'module role',
  boardplacement: 'board placement',
  gearrequest: 'gear request',
  changeorder: 'change order',
  timeentry: 'time entry',
  expensereport: 'expense report',
};

export function capitalize(s: string): string {
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : s;
}

/** `snake_case_value` → `Snake Case Value` (with explicit overrides). */
export function humanizeEnum(value: string | null | undefined): string {
  if (!value) return '';
  const override = ENUM_LABELS[value];
  if (override) return override;
  return value
    .split(/[_\s]+/)
    .filter(Boolean)
    .map((word, i) => (i > 0 && SMALL_WORDS.has(word.toLowerCase()) ? word.toLowerCase() : capitalize(word)))
    .join(' ');
}

/** Lower-case noun for a Django model slug, e.g. `payrollrun` → `payroll run`. */
export function activityModelLabel(modelType: string | null | undefined): string {
  if (!modelType) return '';
  return ACTIVITY_MODEL_LABELS[modelType] ?? modelType;
}

/** `finalize` + `payrollrun` → `Finalize payroll run`. */
export function activityHeadline(action: string, modelType: string): string {
  return `${capitalize(humanizeEnum(action).toLowerCase())} ${activityModelLabel(modelType)}`.trim();
}
